import { X } from "lucide-react";
import { cn } from "@/utils";

export type SuggestBannerTone = "info" | "success" | "warning" | "danger";

export interface SuggestBannerProps {
  tone?: SuggestBannerTone;
  icon: React.ReactNode;
  title: string;
  description?: string;
  actions?: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}

const toneStyles: Record<SuggestBannerTone, { wrap: string; icon: string }> = {
  info: {
    wrap: "bg-[var(--primary-50)] border-[var(--primary-200)]",
    icon: "text-[var(--primary-700)]",
  },
  success: {
    wrap: "bg-[var(--success-bg)] border-[color-mix(in_oklch,var(--success)_25%,var(--border))]",
    icon: "text-[var(--success)]",
  },
  warning: {
    wrap: "bg-[var(--warning-bg)] border-[color-mix(in_oklch,var(--warning)_25%,var(--border))]",
    icon: "text-[var(--warning)]",
  },
  danger: {
    wrap: "bg-[var(--destructive-bg)] border-[color-mix(in_oklch,var(--destructive)_25%,var(--border))]",
    icon: "text-[var(--destructive)]",
  },
};

/**
 * SuggestBanner — inline contextual banner with pulse icon + mandatory CTA.
 * Spec: padding 12px 14px, radius-xl, icon 14px pulseSlow 2.4s, title 12px 600.
 * Used in drawer body / detail panel to nudge user action.
 */
export function SuggestBanner({
  tone = "info",
  icon,
  title,
  description,
  actions,
  dismissible,
  onDismiss,
  className,
}: SuggestBannerProps) {
  const styles = toneStyles[tone];

  return (
    <div
      role="note"
      className={cn(
        "flex items-start gap-3 px-3.5 py-3",
        "rounded-[var(--radius-xl)] border",
        styles.wrap,
        className
      )}
    >
      {/* Pulsing icon */}
      <span
        className={cn(
          "shrink-0 flex items-center justify-center mt-0.5 text-[14px]",
          styles.icon,
          "animate-[pulseSlow_2.4s_ease-in-out_infinite]"
        )}
        aria-hidden
      >
        {icon}
      </span>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-[12px] font-semibold text-[var(--foreground)] leading-snug">
          {title}
        </p>
        {description && (
          <p className="text-[11px] text-[var(--muted-foreground)] mt-0.5 leading-snug">
            {description}
          </p>
        )}
        {actions && (
          <div className="flex items-center gap-1.5 mt-2">
            {actions}
          </div>
        )}
      </div>

      {/* Dismiss */}
      {dismissible && (
        <button
          type="button"
          aria-label="Dismiss suggestion"
          onClick={onDismiss}
          className={cn(
            "shrink-0 inline-flex items-center justify-center",
            "w-5 h-5 rounded-full border-0 p-0",
            "text-[var(--muted-foreground)] hover:text-[var(--foreground)]",
            "hover:bg-[color-mix(in_oklch,var(--foreground)_8%,transparent)]",
            "transition-colors duration-[120ms]"
          )}
        >
          <X size={12} strokeWidth={2} aria-hidden />
        </button>
      )}
    </div>
  );
}
